import { displaySkillUserMessage } from './skillMarkers';
import { displayPromptTemplateUserMessage } from './promptTemplateMarkers';
import { displaySuggestionUserMessage } from './suggestionMarkers';
import { displayDomainMentionUserMessage } from './domainMentionMarkers';

export type ThreadTitleKind = 'chat' | 'skill' | 'prompt' | 'suggestion';

export type ThreadTitlePresentation = {
  kind: ThreadTitleKind;
  /** Skill/template name or full chat title. */
  label: string;
  /** User inputs appended after « — » on skill/template threads. */
  context?: string;
  fallback: string;
};

export type ThreadContextSegment = {
  label?: string;
  value: string;
};

const CONTEXT_FIELD_SEP = /\s+—\s+/;
const LEGACY_CONTEXT_FIELD_SEP = /\s*·\s*/;
const HAS_URL_PROTOCOL_RE = /^[a-z][a-z0-9+.-]*:\/\//i;
const PERCENT_ENCODING_RE = /%[0-9A-Fa-f]{2}/;

function tryDecodeUriComponent(segment: string): string {
  if (!PERCENT_ENCODING_RE.test(segment)) return segment;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** True when a thread-context value should be shown as a URL (LTR + protocol). */
export function isLikelyUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (HAS_URL_PROTOCOL_RE.test(trimmed)) return true;
  if (!/^[\w.-]+\.[\w.-]+/i.test(trimmed)) return false;
  try {
    // eslint-disable-next-line no-new
    new URL(`https://${trimmed}`);
    return true;
  } catch {
    return false;
  }
}

/** Decode percent-encoding and always show http(s) for URL-like values. */
export function formatUrlForDisplay(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (!isLikelyUrl(trimmed)) return trimmed;

  const hasProtocol = HAS_URL_PROTOCOL_RE.test(trimmed);

  try {
    const url = new URL(hasProtocol ? trimmed : `https://${trimmed}`);
    const protocol = hasProtocol ? url.protocol : 'https:';
    const host = url.hostname.replace(/^www\./, '');
    let path =
      url.pathname === '/' ? '' : url.pathname.replace(/\/$/, '');
    if (PERCENT_ENCODING_RE.test(path)) {
      path = path.split('/').map(tryDecodeUriComponent).join('/');
    }
    let search = url.search;
    if (search && PERCENT_ENCODING_RE.test(search)) {
      search = tryDecodeUriComponent(search);
    }
    return `${protocol}//${host}${path}${search}`;
  } catch {
    if (PERCENT_ENCODING_RE.test(trimmed)) {
      return trimmed.split('/').map(tryDecodeUriComponent).join('/');
    }
    return trimmed;
  }
}

/** @deprecated Use {@link formatUrlForDisplay}. */
export function decodeUrlLikeForDisplay(value: string): string {
  return formatUrlForDisplay(value);
}

function parseContextSegment(part: string): ThreadContextSegment | null {
  const trimmed = part.trim();
  if (!trimmed) return null;

  const labeled = /^([^:：]+)[:：]\s*(.+)$/u.exec(trimmed);
  if (labeled) {
    const label = labeled[1].trim();
    const value = labeled[2].trim();
    if (label && value && !/^https?$/i.test(label)) {
      return { label, value };
    }
  }

  return { value: trimmed };
}

/** Skill/template input line for history (labeled fields). */
export function parseThreadContextSegments(
  context: string | null | undefined
): ThreadContextSegment[] {
  const trimmed = context?.trim();
  if (!trimmed) return [];

  const parts = CONTEXT_FIELD_SEP.test(trimmed)
    ? trimmed.split(CONTEXT_FIELD_SEP)
    : trimmed.split(LEGACY_CONTEXT_FIELD_SEP);

  return parts
    .map(parseContextSegment)
    .filter((segment): segment is ThreadContextSegment => segment != null)
    .map(segment => ({
      ...segment,
      value: formatUrlForDisplay(segment.value)
    }));
}

const SKILL_TITLE_RE =
  /^اجرای مهارت «([^»]+)»(?:\s*—\s*(.+))?$/;
const PROMPT_TITLE_RE =
  /^اجرای قالب «([^»]+)»(?:\s*—\s*(.+))?$/;
const SUGGESTION_TITLE_RE =
  /^سوالات متداول «([^»]+)»(?:\s*—\s*(.+))?$/;

const EXCLUDED_FIELD_LABELS = new Set(['http', 'https', 'ftp']);

const LABELED_FIELD_HEAD_RE =
  /(?:^|\s)([\u0600-\u06FF][\u0600-\u06FF\w\s‌]*|[A-Za-z][A-Za-z0-9\s]{1,40})[:：]\s*/gu;

function parseCollapsedLabeledFields(
  text: string
): ThreadContextSegment[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  const hits: Array<{
    label: string;
    valueStart: number;
    matchStart: number;
  }> = [];

  for (const match of trimmed.matchAll(LABELED_FIELD_HEAD_RE)) {
    const label = match[1]?.trim();
    if (!label || EXCLUDED_FIELD_LABELS.has(label.toLowerCase())) continue;
    const matchStart = match.index ?? 0;
    hits.push({
      label,
      valueStart: matchStart + match[0].length,
      matchStart
    });
  }

  if (hits.length === 0) return [];

  const segments: ThreadContextSegment[] = [];
  for (let i = 0; i < hits.length; i++) {
    const end =
      i + 1 < hits.length ? hits[i + 1].matchStart : trimmed.length;
    const value = trimmed.slice(hits[i].valueStart, end).trim();
    if (!value) continue;
    segments.push({ label: hits[i].label, value });
  }

  return segments;
}

/** Legacy FAQ threads: full prompt collapsed to one line with «label: value» fields. */
function tryParseLegacyFaqCollectTitle(
  cleaned: string
): ThreadTitlePresentation | null {
  const trimmed = cleaned.trim();
  if (!trimmed) return null;

  const first = LABELED_FIELD_HEAD_RE.exec(trimmed);
  LABELED_FIELD_HEAD_RE.lastIndex = 0;
  if (!first || first.index == null) return null;

  const label = first[1]?.trim();
  if (!label || EXCLUDED_FIELD_LABELS.has(label.toLowerCase())) return null;

  const intro = trimmed.slice(0, first.index).trim();
  if (intro.length < 12) return null;

  const segments = parseCollapsedLabeledFields(trimmed);
  if (segments.length === 0) return null;

  const context = segments
    .map(segment =>
      segment.label
        ? `${segment.label}: ${segment.value}`
        : segment.value
    )
    .join(' — ');

  return {
    kind: 'suggestion',
    label: intro,
    context,
    fallback: trimmed
  };
}

export function cleanThreadTitle(title: string | null | undefined): string {
  const cleaned =
    displayDomainMentionUserMessage(
      displaySuggestionUserMessage(
        displayPromptTemplateUserMessage(displaySkillUserMessage(title))
      )
    ) || (title ?? '').trim();
  return cleaned.trim() || 'گفتگوی بدون عنوان';
}

export function parseThreadTitlePresentation(
  cleanedTitle: string
): ThreadTitlePresentation {
  const cleaned = cleanedTitle.trim() || 'گفتگوی بدون عنوان';

  const skill = cleaned.match(SKILL_TITLE_RE);
  if (skill) {
    const name = skill[1].trim();
    const context = skill[2]?.trim();
    return {
      kind: 'skill',
      label: name || cleaned,
      context: context || undefined,
      fallback: cleaned
    };
  }

  const prompt = cleaned.match(PROMPT_TITLE_RE);
  if (prompt) {
    const name = prompt[1].trim();
    const context = prompt[2]?.trim();
    return {
      kind: 'prompt',
      label: name || cleaned,
      context: context || undefined,
      fallback: cleaned
    };
  }

  const suggestion = cleaned.match(SUGGESTION_TITLE_RE);
  if (suggestion) {
    const name = suggestion[1].trim();
    const context = suggestion[2]?.trim();
    return {
      kind: 'suggestion',
      label: name || cleaned,
      context: context || undefined,
      fallback: cleaned
    };
  }

  const legacyFaq = tryParseLegacyFaqCollectTitle(cleaned);
  if (legacyFaq) return legacyFaq;

  return { kind: 'chat', label: cleaned, fallback: cleaned };
}

export function getThreadTitlePresentation(
  title: string | null | undefined
): ThreadTitlePresentation {
  return parseThreadTitlePresentation(cleanThreadTitle(title));
}

/** Plain line for modals, search snippets, aria-label. */
export function formatThreadTitleLine(title: string | null | undefined): string {
  const pres = getThreadTitlePresentation(title);
  if (pres.kind === 'chat' && !pres.context) return pres.label;
  if (pres.context) {
    const contextLine = parseThreadContextSegments(pres.context)
      .map(segment =>
        segment.label
          ? `${segment.label}: ${segment.value}`
          : segment.value
      )
      .join(' — ');
    return `${pres.label} — ${contextLine}`;
  }
  return pres.label;
}
